import { useReducer, useRef } from 'react'

type DialogState<TState> = {
  open: boolean
} & Partial<TState>

type DialogCallbacks<TState> = {
  open: (payload?: TState) => void
  close: () => void
}

type UseDialogResult<TState> = [
  state: DialogState<TState>,
  open: DialogCallbacks<TState>['open'],
  close: DialogCallbacks<TState>['close'],
]

type ModalAction<TState> =
  | { type: 'open'; payload?: TState }
  | { type: 'close' }

function dialogReducer<TState>(
  state: DialogState<TState>,
  action: ModalAction<TState>,
): DialogState<TState> {
  switch (action.type) {
    case 'open': {
      return {
        ...action.payload,
        open: true,
      } as DialogState<TState>
    }
    case 'close':
      return {
        ...state,
        open: false,
      }
    default:
      return state
  }
}

/**
 * useDialog is a util hook to open modal, dialog, popups with custom props
 * It returns tuple of modal props, open callback, close callback
 *
 * Example:
 *
 *  type DialogParams = {
 *    instanceId: string
 *  }
 *
 *  const MyComponent: FC = () => {
 *    const [instanceDialog, openInstanceDialog, closeInstanceDialog] = useDialog<DialogParams>();
 *
 *    return (
 *      <>
 *        <button onClick={() => openInstanceDialog({ instanceId: some_instance_id })}>edit instance</button>
 *        <Button onClick={() => openInstanceDialog()}>create instance</button>
 *        <InstanceDialog {...instanceDialog} onClose={closeInstanceDialog} />
 *      </>
 *    )
 *  }
 */

const useDialog = <TState extends Record<string, unknown>>(
  initialState?: TState,
): UseDialogResult<TState> => {
  const callbacks = useRef<DialogCallbacks<TState>>()
  const [state, dispatch] = useReducer(
    dialogReducer,
    initialState,
    (props?: TState) => ({
      ...props,
      open: false,
    }),
  )

  if (!callbacks.current) {
    callbacks.current = {
      open: (payload?: TState) => {
        dispatch({
          type: 'open',
          payload,
        })
      },
      close: () => {
        dispatch({
          type: 'close',
        })
      },
    }
  }
  return [state, callbacks.current.open, callbacks.current.close]
}

export default useDialog
